import type { Socket } from 'socket.io-client';
import type { AppDispatch } from 'src/state';
import type { WebsocketRole } from 'src/state/websocket/types';

import { io } from 'socket.io-client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { store } from 'src/state';
import { CONFIG } from 'src/global-config';
import { addMessage } from 'src/state/messages';
import { updateJobProgress } from 'src/state/jobs/jobs-slice';
import {
  setError,
  joinSuccess,
  leaveSuccess,
  connectSuccess,
  connectFailure,
  disconnect as disconnectAction,
} from 'src/state/websocket/websocket-slice';

import { toast } from 'src/components/snackbar';

let socket: Socket | null = null;
let dispatch: AppDispatch | null = null;

/**
 * Initialize the Socket.io client with Redux store dispatch.
 * Does NOT auto-connect — connection is deferred until after authentication
 * so the WebSocket handshake always includes the auth cookie.
 * Should be called once during app initialization.
 */
export const initializeSocket = (storeDispatch: AppDispatch): void => {
  dispatch = storeDispatch;

  // Create the socket instance and register event handlers,
  // but do not connect yet (autoConnect: false).
  getSocket();
};

/**
 * Initialize and return the Socket.io client instance
 * Connects to the changes service URL from configuration
 */
export const getSocket = (): Socket => {
  if (!socket) {
    // Connect to changes service using configured URL
    // In development: defaults to http://localhost:8090
    // In production: uses https://app.lumi.education with /changes path
    socket = io(CONFIG.changesUrl, {
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      withCredentials: true,
      // Use /changes/socket.io path for reverse proxy routing
      path: '/changes/socket.io',
      // Use WebSocket transport (preferred) with polling fallback
      // Both Cloud Run and Cloudflare support WebSockets
      transports: ['websocket', 'polling'],
    });

    // Connection event handlers
    socket.on('connect', () => {
      console.log('[Socket.io] Connected to changes service, socket ID:', socket?.id);
      if (dispatch && socket?.id) {
        dispatch(connectSuccess({ socketId: socket.id }));
      }
      // Rejoin all previously joined rooms from Redux state
      const state = store.getState();
      const { rooms } = state.websocket;
      if (rooms.length > 0) {
        console.log('[Socket.io] Rejoining', rooms.length, 'rooms after connection');
        rooms.forEach(({ domain, room, access }) => {
          socket?.emit('join', domain, room, access);
        });
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.io] Disconnected:', reason);
      if (dispatch) {
        dispatch(disconnectAction());
      }
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket.io] Connection error:', error);
      if (error.message === 'lumi.changes.auth.error.token-expired') {
        socket!.io.opts.reconnection = false;
        window.location.reload();
        return;
      }
      if (dispatch) {
        dispatch(connectFailure({ error: error.message }));
      }
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket.io] Reconnected after', attemptNumber, 'attempts');
      if (dispatch && socket?.id) {
        dispatch(connectSuccess({ socketId: socket.id }));
      }
      // Rejoin all previously joined rooms from Redux state after reconnection
      const state = store.getState();
      const { rooms } = state.websocket;
      if (rooms.length > 0) {
        console.log('[Socket.io] Rejoining', rooms.length, 'rooms after reconnection');
        rooms.forEach(({ domain, room, access }) => {
          socket?.emit('join', domain, room, access);
        });
      }
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('[Socket.io] Reconnection attempt:', attemptNumber);
    });

    socket.on('reconnect_error', (error) => {
      console.error('[Socket.io] Reconnection error:', error);
      if (dispatch) {
        dispatch(setError({ error: error.message }));
      }
    });

    socket.on('reconnect_failed', () => {
      console.error('[Socket.io] Reconnection failed');
      if (dispatch) {
        dispatch(connectFailure({ error: 'Reconnection failed' }));
      }
    });

    // Room join/leave events
    socket.on('joined', () => {
      console.log('[Socket.io] Joined room');
    });

    socket.on('left', () => {
      console.log('[Socket.io] Left room');
    });

    socket.on('error', (error) => {
      console.error('[Socket.io] Socket error:', error);
      if (dispatch) {
        dispatch(setError({ error: error.message || String(error) }));
      }
    });

    // Message received event
    socket.on(
      'message_received',
      (data: {
        message: {
          id: string;
          sender_id: string;
          title: string;
          type: string;
          category: string;
          isRead: boolean;
          createdAt: number;
          avatarUrl: string | null;
          toastSettings?: {
            showToast?: boolean;
            duration?: number;
            variant?: 'info' | 'success' | 'warning' | 'error';
            action?: {
              label: string;
              onClick: string;
            };
            cancelLabel?: string;
          };
        };
      }) => {
        console.log('[Socket.io] Message received:', data.message);
        if (dispatch) {
          dispatch(addMessage(data.message));
        }

        // Handle toast notification based on settings
        const toastSettings = data.message.toastSettings || {};
        const shouldShowToast = toastSettings.showToast !== false; // Default true

        if (!shouldShowToast) {
          return;
        }

        // Strip HTML tags from title for toast display
        const plainTextTitle = data.message.title.replace(/<[^>]*>/g, '');

        // Determine toast variant
        const defaultVariant = data.message.type === 'info' ? 'info' : 'success';
        const variant = toastSettings.variant || defaultVariant;

        // Determine duration (default 4000ms, supports Infinity for persistent toasts)
        const duration = toastSettings.duration ?? 4000;

        // Generate unique ID for this toast
        const toastId = `message-${data.message.id}`;

        // Build toast options
        const toastOptions: any = {
          id: toastId,
          position: 'top-right',
          duration,
          closeButton: false, // We'll use custom action buttons
          style: {
            marginTop: '64px',
          },
        };

        // Add action buttons if specified
        if (toastSettings.action || toastSettings.cancelLabel) {
          const buttonColor =
            variant === 'error'
              ? 'error'
              : variant === 'warning'
                ? 'warning'
                : variant === 'success'
                  ? 'success'
                  : 'info';

          toastOptions.action = (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {toastSettings.action && (
                <Button
                  size="small"
                  color={buttonColor}
                  onClick={() => {
                    // Handle URL navigation or action
                    const actionUrl = toastSettings.action!.onClick;

                    // For export downloads, navigate directly (server sends Content-Disposition header)
                    if (actionUrl.includes('/export/')) {
                      window.location.href = actionUrl;
                    } else if (
                      actionUrl.startsWith('http://') ||
                      actionUrl.startsWith('https://')
                    ) {
                      window.open(actionUrl, '_blank');
                    } else if (actionUrl.startsWith('/')) {
                      window.location.href = actionUrl;
                    } else {
                      // Could be a custom action identifier
                      console.log('[Toast Action]:', actionUrl);
                    }
                    toast.dismiss(toastId);
                  }}
                >
                  {toastSettings.action.label}
                </Button>
              )}
              {toastSettings.cancelLabel && (
                <Button
                  size="small"
                  onClick={() => {
                    console.log('[Toast] Dismissed');
                    toast.dismiss(toastId);
                  }}
                >
                  {toastSettings.cancelLabel}
                </Button>
              )}
            </Box>
          );
        }

        // Show toast with appropriate variant
        switch (variant) {
          case 'success':
            toast.success(plainTextTitle, toastOptions);
            break;
          case 'warning':
            toast.warning(plainTextTitle, toastOptions);
            break;
          case 'error':
            toast.error(plainTextTitle, toastOptions);
            break;
          case 'info':
          default:
            toast.info(plainTextTitle, toastOptions);
            break;
        }
      }
    );

    // Job update event
    socket.on(
      'job_update',
      (data: {
        jobId: string;
        user_id: string;
        progress?: number;
        status: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
        type: 'content_import' | 'content_export';
        error?: string;
        result?: string;
        metadata?: {
          contentId?: string;
          contentType?: 'htmlexternal' | 'htmlbundle' | 'scorm' | 'h5p';
          workspaceId?: string;
          folderId?: string;
          key?: string;
        };
      }) => {
        console.log('[Socket.io] Job update received:', data);
        if (dispatch) {
          dispatch(
            updateJobProgress({
              jobId: data.jobId,
              status: data.status,
              progress: data.progress,
              error: data.error,
              result: data.result,
              updated_at: new Date().toISOString(),
            })
          );
        }
      }
    );
  }

  return socket;
};

/**
 * Connect the socket
 */
export const connectSocket = (): void => {
  const sock = getSocket();
  if (sock && !sock.connected) {
    sock.connect();
  }
};

/**
 * Disconnect and cleanup the Socket.io client
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  if (dispatch) {
    dispatch(disconnectAction());
  }
};

/**
 * Join a room
 * The room is tracked in Redux state and will be automatically rejoined on reconnection
 */
export const joinRoom = (domain: string, room: string, access: WebsocketRole): void => {
  const sock = getSocket();

  if (sock && sock.connected) {
    sock.emit('join', domain, room, access);
    if (dispatch) {
      dispatch(joinSuccess({ domain, room, access }));
    }
  } else {
    console.warn('[Socket.io] Room join queued - will join when socket connects');
    if (dispatch) {
      // Still track the room in Redux even if not connected yet
      dispatch(joinSuccess({ domain, room, access }));
    }
  }
};

/**
 * Leave a room
 * Removes the room from Redux state so it won't be rejoined on reconnection
 */
export const leaveRoom = (domain: string, room: string, access: WebsocketRole): void => {
  const sock = getSocket();

  if (sock && sock.connected) {
    sock.emit('leave', domain, room, access);
  }

  // Always update Redux state to remove the room
  if (dispatch) {
    dispatch(leaveSuccess({ domain, room, access }));
  }
};

/**
 * Check if socket is connected
 */
export const isSocketConnected = (): boolean => socket?.connected ?? false;

/**
 * Wait for socket to connect
 * Returns a promise that resolves when the socket is connected
 * If already connected, resolves immediately
 */
export const waitForConnection = (): Promise<void> =>
  new Promise((resolve) => {
    const sock = getSocket();
    if (sock.connected) {
      resolve();
    } else {
      const onConnect = () => {
        sock.off('connect', onConnect);
        resolve();
      };
      sock.on('connect', onConnect);
    }
  });
